import React from 'react';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Alert,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';

const TestRoutesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuthContext();

  const testRoutes = [
    // Мероприятия
    { path: '/events', label: 'Мероприятия - Список', category: 'События' },
    { path: '/events/create', label: 'Создать мероприятие', category: 'События' },
    { path: '/events/calendar', label: 'Календарь мероприятий', category: 'События' },
    { path: '/calendar', label: 'Календарь (редирект → /events/calendar)', category: 'События' },
    
    // Участники
    { path: '/analytics/participants', label: 'Участники', category: 'Управление' },
    
    // Задачи
    { path: '/tasks', label: 'Задачи - Список', category: 'Управление' },
    { path: '/tasks/create', label: 'Создать задачу', category: 'Управление' },
    
    // Пользователи
    { path: '/users', label: 'Пользователи', category: 'Управление' },
    { path: '/users/create', label: 'Создать пользователя', category: 'Управление' },
    
    // Маркетплейс
    { path: '/marketplace', label: 'Маркетплейс - Главная', category: 'Маркетплейс' },
    { path: '/marketplace/search', label: 'Поиск поставщиков', category: 'Маркетплейс' },
    { path: '/marketplace/quotation', label: 'Запрос предложений (редирект → /marketplace)', category: 'Маркетплейс' },
    
    // Сообщество
    { path: '/community', label: 'Сообщество - Главная', category: 'Сообщество' },
    { path: '/community/ask', label: 'Задать вопрос (редирект → /community)', category: 'Сообщество' },
    { path: '/community/experts', label: 'Эксперты (редирект → /community)', category: 'Сообщество' },
    
    // Коммуникации
    { path: '/chats', label: 'Чаты', category: 'Коммуникации' },
    { path: '/video-calls', label: 'Видеозвонки', category: 'Коммуникации' },
    { path: '/communications', label: 'Коммуникации', category: 'Коммуникации' },
    
    // Другие разделы
    { path: '/analytics', label: 'Аналитика', category: 'Аналитика' },
    { path: '/documents', label: 'Документы', category: 'Документы' },
    { path: '/orders', label: 'Заказы', category: 'Управление' },
    { path: '/collaboration', label: 'Коллаборация', category: 'Коллаборация' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        🔧 Диагностика маршрутизации Event Master
      </Typography>
      
      {/* Системная информация */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Системная информация
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2">Текущий путь:</Typography>
              <Chip label={location.pathname} color="primary" variant="outlined" />
            </Box>
            
            <Box>
              <Typography variant="subtitle2">Статус авторизации:</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {isLoading ? (
                  <Chip label="Загрузка..." color="default" />
                ) : isAuthenticated ? (
                  <>
                    <Chip label="Авторизован" color="success" icon={<CheckIcon />} />
                    <Chip label={`Роль: ${user?.role || 'Не определена'}`} variant="outlined" />
                    <Chip label={`Пользователь: ${(user as any)?.fullName || user?.email || 'Неизвестен'}`} variant="outlined" />
                  </>
                ) : (
                  <Chip label="Не авторизован" color="error" icon={<ErrorIcon />} />
                )}
              </Box>
            </Box>
            
            <Box>
              <Typography variant="subtitle2">Токен в localStorage (accessToken):</Typography>
              <Chip
                label={localStorage.getItem('accessToken') ? 'Присутствует' : 'Отсутствует'}
                color={localStorage.getItem('accessToken') ? 'success' : 'error'}
                variant="outlined"
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Инструкция:</strong> Нажмите "Тест" рядом с любым маршрутом для проверки. 
        Если появляется ошибка 404 или форма авторизации - это указывает на проблему с роутом.
      </Alert>

      {/* Группировка роутов по категориям */}
      {Object.entries(
        testRoutes.reduce((acc, route) => {
          const bucket = acc[route.category] ?? [];
          bucket.push(route);
          acc[route.category] = bucket;
          return acc;
        }, {} as Record<string, typeof testRoutes>)
      ).map(([category, routes]) => (
        <Card key={category} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              📂 {category}
            </Typography>
            
            <List dense>
              {routes.map((route, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary={route.label}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {route.path}
                        </Typography>
                      </Box>
                    }
                  />
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => {
                      navigate(route.path);
                    }}
                    sx={{ ml: 1 }}
                  >
                    Тест
                  </Button>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}

      <Box mt={3} sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          onClick={() => navigate('/dashboard')}
        >
          Вернуться на дашборд
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => {
            // eslint-disable-next-line no-console
            console.clear();
          }}
        >
          Очистить консоль
        </Button>
        <Button 
          variant="outlined" 
          color="warning"
          onClick={() => {
            localStorage.clear();
            window.location.reload();
          }}
        >
          Сбросить localStorage
        </Button>
      </Box>
    </Container>
  );
};

export default TestRoutesPage; 